import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { isEmpty } from 'lodash';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
} from 'antd';

import {
  useAddressQuery,
  useDebounce,
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
  useDisclosure,
} from '@/hooks';

import { applyMask, removeMask } from '@/utils/mask';

import {
  CustomerGeneral,
  GenderType,
  PersonType,
} from '@/core/domain/people/Customers/Customers.types';

import { CustomerAddress } from '@/core/domain/people/CustomerAddresses/CustomerAddresses.types';

import {
  CnpjFormField,
  CpfFormField,
  FloatingFormItem,
  InputDate,
  PhoneFormField,
  ZipFormField,
} from '@/components/_common';
import { cepMask } from '@/utils/mask/commonMasks';
import { useImportedCpfCnpj } from '@/core/domain/people/ImportedCPFCNPJ/ImportedCPFCNPJ.hook';
import validateCpf from '@/utils/functions/validateCpf';
import { validateCnpj } from '@/utils/functions/validateCnpj';
import moment, { Moment } from 'moment';
import CustomerSetMainAddressModal from '../CustomerSetMainAddressModal';
import RecommendationSelect from '@/components/_common/Recommendation/RecommendationSelect';
import CustomerAddressCollapse from '../CustomerAddressCollapse';
import CustomerContactCollapse from '../CustomerContactCollapse';
import { isAppPortocar } from '@/index';
import useValidateNoWhiteSpace from '@/hooks/useValidateNoWhiteSpace';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import CountrySelectField from '@/components/_common/CountrySelectField';
import { removeCpfCnpjMask } from '@/utils/mask/removeMask';

export interface CustomerSimplifiedFormData {
  michelinSegmentCode?: string;
  name: string;
  personType: PersonType;
  cpfCnpj?: string;
  fantasyName?: string;
  genderType?: GenderType;
  stateRegistration?: string;
  municipalRegistration?: string;
  email?: string;
  phone?: string;
  retentionIRPJorCSLL?: boolean;
  retentionISS?: boolean;
  enableLGPD?: boolean | null;
  address?: {
    cityId?: number;
    city?: string;
    complement?: string;
    district?: string;
    number?: string;
    street?: string;
    zipCode?: string;
    state?: string;
  };
  phoneId?: number;
  birthDate?: Moment;
  recommendation?: { id: number };
  foreignDocument?: string;
}

interface CustomerSimplifiedFormProps {
  form: FormInstance<CustomerSimplifiedFormData>;
  address?: CustomerAddress;
  isEdit?: boolean;
  mainAddress?: boolean;
  customerId?: number;
  customer?: CustomerGeneral;
  disableAll?: boolean;
  recommendationId?: number;
}

function CustomerSimplifiedForm({
  form,
  address,
  isEdit,
  customerId,
  customer,
  mainAddress,
  disableAll,
  recommendationId,
}: CustomerSimplifiedFormProps) {
  const intl = useIntl();
  const [modalSetMainAddressOpened, modalSetMainAddressAction] =
    useDisclosure();

  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();

  const { state, city, zipCode } = useAddressQuery(!isEdit);

  const enableLGPD = Form.useWatch('enableLGPD', form);
  const { mutateAsync } = useImportedCpfCnpj();

  const zipCodeInput = Form.useWatch(['address', 'zipCode'], form);
  const [disabled, setDisable] = useState(true);

  const personTypeField = Form.useWatch('personType', form);

  const isPersonTypeNatural = personTypeField === PersonType.Natural;

  const cpfCnpj = Form.useWatch('cpfCnpj', form);
  const foreignDocument = Form.useWatch('foreignDocument', form);
  const cpfCnpjdebounce = useDebounce(cpfCnpj);

  const matrix = useMatrix();
  const shouldFilterMichelin = matrix.data?.matrixSettings.michelinIntegration;

  const handleType = () => {
    form.resetFields([
      'cpfCnpj',
      'name',
      'genderType',
      'fantasyName',
      'stateRegistration',
    ]);
  };

  useEffect(() => {
    if (zipCode.data && !isEmpty(zipCode.data)) {
      state.setSelectedStateCode(zipCode.data.state);

      setDisable(false);

      const address = form.getFieldValue('address');

      form.setFieldsValue({
        ...form.getFieldsValue(),
        address: {
          cityId: zipCode.data.cityId,
          street: !!zipCode.data.street ? zipCode.data.street : address?.street,
          district: !!zipCode.data.district
            ? zipCode.data.district
            : address?.district,
          state: zipCode.data.state,
          city: zipCode.data.cityName,
        },
      });
    }
  }, [zipCode.data]);

  useEffect(() => {
    if (zipCodeInput && zipCodeInput.length < 8) {
      setDisable(true);
      form.setFieldsValue({
        address: undefined,
      });
    }
  }, [zipCodeInput]);

  useEffect(() => {
    if (address) {
      city.setSelectedCityId(address.cityId);

      handleZipChange({
        address: { ...address, zipCode: removeMask(address.zipCode) },
      });

      form.setFieldsValue({
        ...form.getFieldsValue(),
        address: {
          zipCode: applyMask(removeMask(address.zipCode), cepMask),
          street: address.street,
          cityId: address.cityId,
          district: address.district,
        },
      });
    } else {
      city.setSelectedCityId(undefined);
    }
  }, [address]);

  useEffect(() => {
    if (city.selectedCity && address) {
      state.setSelectedStateCode(city.selectedCity.state);

      form.setFieldsValue({
        ...form.getFieldsValue(),
        address: {
          cityId: city.selectedCity.id,
          state: city.selectedCity.state,
        },
      });
    }
  }, [city.selectedCity, address]);

  // eslint-disable-next-line
  const handleFormValuesChange = (valuesChanged: any) => {
    handleZipChange(valuesChanged);
    handleStateChange(valuesChanged);
  };

  // eslint-disable-next-line
  const handleZipChange = (valuesChanged: any) => {
    if (!valuesChanged?.address?.zipCode) return;

    const { zipCode: search } = valuesChanged.address;
    zipCode.setSelectedZipCode(undefined);

    if (!search || search.length !== 8) return;

    zipCode.setSelectedZipCode(search);
  };

  // eslint-disable-next-line
  const handleStateChange = (valuesChanged: any) => {
    if (!valuesChanged?.address?.state) return;

    form.resetFields([['address', 'cityId']]);

    state.setSelectedStateCode(valuesChanged.state);
  };

  const handleCpfCnpj = async (value: string | undefined) => {
    const mask = removeCpfCnpjMask(value);
    const isValidCpf = validateCpf(mask);
    const isValidCnpj = validateCnpj(mask);

    if (isValidCpf || isValidCnpj) {
      const data = await mutateAsync(mask);

      const name = (isPersonTypeNatural ? !data.name : !data.officialName)
        ? form.getFieldValue('name')
        : isValidCpf
        ? data.name
        : data.officialName;

      const genderType = !data.gender
        ? form.getFieldValue('genderType')
        : data.gender === 'M'
        ? GenderType.Man
        : GenderType.Woman;

      form.setFieldsValue({
        name,
        genderType,
        fantasyName: data.tradeName,
        stateRegistration: data.stateRegistration,
        birthDate: data.birthDate
          ? moment(data.birthDate)
          : form.getFieldValue('birthDate'),
      });
    }
  };

  useEffect(() => {
    if (!isEdit) {
      handleCpfCnpj(cpfCnpjdebounce);
    }
  }, [cpfCnpjdebounce, isEdit]);

  return (
    <>
      <Form
        form={form}
        onValuesChange={handleFormValuesChange}
        layout="vertical"
      >
        <Form.Item hidden name="id" />
        <Form.Item hidden name="phoneId" />

        <Row gutter={[16, 8]}>
          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              name="personType"
              label={intl.formatMessage({ id: 'customers.domain.personType' })}
              initialValue={PersonType.Natural}
              getValueFromEvent={(value) => {
                if (!isEdit) {
                  handleType();
                }

                return value;
              }}
            >
              <Select
                style={{ width: '100%' }}
                disabled={disableAll || (isEdit && isAppPortocar)}
              >
                {Object.values(PersonType).map((type) => (
                  <Select.Option key={type} value={type}>
                    {intl.formatMessage({
                      id: `customers.domain.personType.${type.toLowerCase()}`,
                    })}
                  </Select.Option>
                ))}
              </Select>
            </FloatingFormItem>
          </Col>

          <Form.Item
            shouldUpdate={(prev, curr) => prev.personType !== curr.personType}
            noStyle
          >
            {isPersonTypeNatural ? (
              <>
                {!foreignDocument && (
                  <Col span={24} md={12} lg={8}>
                    <CpfFormField
                      label="CPF"
                      fieldName="cpfCnpj"
                      required={isAppPortocar}
                      notRequired={!isAppPortocar}
                      disabled={
                        (isAppPortocar &&
                          isEdit &&
                          customer?.cpfCnpj?.length === 11 &&
                          validateCpf(customer?.cpfCnpj)) ||
                        disableAll
                      }
                    />
                  </Col>
                )}

                {!cpfCnpj && (
                  <Col span={24} md={12} lg={8}>
                    <FloatingFormItem
                      name="foreignDocument"
                      label="Doc. identificação estrangeiro"
                      rules={
                        isAppPortocar && !cpfCnpj
                          ? [defaultEmptyFieldError]
                          : []
                      }
                    >
                      <Input
                        disabled={
                          isEdit && isAppPortocar && !!customer?.foreignDocument
                        }
                      />
                    </FloatingFormItem>
                  </Col>
                )}

                <Col span={24} md={12} lg={8}>
                  <FloatingFormItem
                    name="name"
                    label={intl.formatMessage({ id: 'customers.domain.name' })}
                    rules={[defaultEmptyFieldError]}
                  >
                    <Input disabled={disableAll} />
                  </FloatingFormItem>
                </Col>

                <Col span={24} md={12} lg={8}>
                  <FloatingFormItem
                    name="genderType"
                    label={intl.formatMessage({
                      id: 'customers.domain.genderType',
                    })}
                  >
                    <Select style={{ width: '100%' }} disabled={disableAll}>
                      {Object.values(GenderType).map((type) => (
                        <Select.Option key={type} value={type}>
                          {intl.formatMessage({
                            id: `customers.domain.genderType.${type.toLowerCase()}`,
                          })}
                        </Select.Option>
                      ))}
                    </Select>
                  </FloatingFormItem>
                </Col>

                <Col span={24} md={12} lg={8}>
                  <FloatingFormItem
                    name="birthDate"
                    label={intl.formatMessage({
                      id: 'customers.domain.birthDate',
                    })}
                    rules={isAppPortocar ? [defaultEmptyFieldError] : []}
                  >
                    <InputDate disabled={disableAll} />
                  </FloatingFormItem>
                </Col>
              </>
            ) : (
              <>
                {!foreignDocument && (
                  <Col span={24} md={12} lg={8}>
                    <CnpjFormField
                      label="CNPJ"
                      fieldName="cpfCnpj"
                      required={isAppPortocar}
                      notRequired={!isAppPortocar}
                      disabled={
                        (isAppPortocar && isEdit && !!customer?.cpfCnpj) ||
                        disableAll
                      }
                    />
                  </Col>
                )}

                {!cpfCnpj && (
                  <Col span={24} md={12} lg={8}>
                    <FloatingFormItem
                      name="foreignDocument"
                      label="Doc. identificação estrangeiro"
                      rules={
                        isAppPortocar && !cpfCnpj
                          ? [defaultEmptyFieldError]
                          : []
                      }
                    >
                      <Input
                        disabled={
                          isEdit && isAppPortocar && !!customer?.foreignDocument
                        }
                      />
                    </FloatingFormItem>
                  </Col>
                )}

                <Col span={24} md={12} lg={8}>
                  <FloatingFormItem
                    name="name"
                    label={intl.formatMessage({ id: 'customers.domain.name' })}
                    rules={[defaultEmptyFieldError]}
                  >
                    <Input disabled={disableAll} />
                  </FloatingFormItem>
                </Col>

                <Col span={24} md={12} lg={8}>
                  <FloatingFormItem
                    name="fantasyName"
                    label={intl.formatMessage({
                      id: 'customers.domain.fantasyName',
                    })}
                  >
                    <Input disabled={disableAll} />
                  </FloatingFormItem>
                </Col>

                <Col span={24} md={12} lg={8}>
                  <FloatingFormItem
                    name="municipalRegistration"
                    label={intl.formatMessage({
                      id: 'customers.domain.municipalRegistration',
                    })}
                  >
                    <Input disabled={disableAll} />
                  </FloatingFormItem>
                </Col>

                <Col span={24} md={12} lg={8}>
                  <FloatingFormItem
                    name="stateRegistration"
                    label={intl.formatMessage({
                      id: 'customers.domain.stateRegistration',
                    })}
                  >
                    <Input disabled={disableAll} />
                  </FloatingFormItem>
                </Col>
              </>
            )}
          </Form.Item>

          {shouldFilterMichelin && (
            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name="michelinSegmentCode"
                label={intl.formatMessage({
                  id: 'customers.domain.michelinSegmentCode',
                })}
                rules={[
                  defaultEmptyFieldError,
                  useValidateNoWhiteSpace(),
                  defaultMaxLenghtFieldError(3),
                ]}
              >
                <Input />
              </FloatingFormItem>
            </Col>
          )}

          {!mainAddress && isEdit && (
            <Col span={24}>
              <Alert
                style={{ marginBottom: 16 }}
                showIcon
                type="warning"
                description={
                  <Button type="link" onClick={modalSetMainAddressAction.open}>
                    Definir endereço principal
                  </Button>
                }
              />
            </Col>
          )}

          {!isEdit && (
            <>
              <Col span={24} md={12} lg={8}>
                <PhoneFormField
                  fieldName="phone"
                  label="Celular"
                  required={isAppPortocar}
                  disabled={disableAll}
                />
              </Col>

              <Col span={24} md={12} lg={8}>
                <FloatingFormItem
                  name="email"
                  label={intl.formatMessage({
                    id: 'customers.domain.contact.contactType.email',
                  })}
                  rules={[
                    {
                      required: isAppPortocar,
                      type: 'email',
                      message: intl.formatMessage({
                        id: 'commons.form.errors.invalidEmail',
                      }),
                    },
                  ]}
                >
                  <Input disabled={disableAll} />
                </FloatingFormItem>
              </Col>

              <Col span={24} md={12} lg={8}>
                <FloatingFormItem
                  name="countryOrigin"
                  label="País"
                  rules={!!foreignDocument ? [defaultEmptyFieldError] : []}
                >
                  <CountrySelectField
                    allowClear
                    isForeignDocument={!!foreignDocument}
                  />
                </FloatingFormItem>
              </Col>

              <Col span={24}>
                <FloatingFormItem
                  name={['recommendation', 'id']}
                  label={
                    <FormattedMessage id="customers.form.recommendation" />
                  }
                  initialValue={recommendationId}
                >
                  <RecommendationSelect />
                </FloatingFormItem>
              </Col>
            </>
          )}
        </Row>

        {isEdit && <CustomerContactCollapse customerId={customerId} />}

        {isEdit && !!customerId && (
          <CustomerAddressCollapse customerId={customerId} />
        )}

        <Divider />

        {!isEdit && (
          <Row gutter={[16, 8]}>
            <Col span={24} md={12} lg={8}>
              <ZipFormField
                disable={disableAll}
                fieldName={['address', 'zipCode']}
                label={intl.formatMessage({
                  id: 'insurers.addresses.form.zipCode',
                })}
                required={isAppPortocar}
              />
              <Form.Item hidden name={['address', 'main']} />
            </Col>

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['address', 'state']}
                label={intl.formatMessage({
                  id: 'insurers.addresses.form.id',
                })}
              >
                <Select
                  disabled
                  allowClear
                  showSearch
                  loading={state.isFetching}
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label as string)
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  options={state.list.map((state) => ({
                    value: state.code,
                    label: state.code,
                  }))}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={8}>
              <Form.Item hidden name={['address', 'city']}>
                <Input />
              </Form.Item>

              <FloatingFormItem
                name={['address', 'cityId']}
                label={intl.formatMessage({
                  id: 'insurers.addresses.form.cityId',
                })}
                getValueFromEvent={(value) => {
                  form.setFieldValue(
                    ['address', 'city'],
                    city.list.find((item) => item.id === value)?.name,
                  );

                  return value;
                }}
              >
                <Select
                  disabled
                  allowClear
                  showSearch
                  loading={city.isFetching}
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label as string)
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  options={city.list.map((c) => ({
                    value: c.id,
                    label: c.name,
                  }))}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['address', 'district']}
                rules={disabled || disableAll ? [] : [defaultEmptyFieldError]}
                label={intl.formatMessage({
                  id: 'insurers.addresses.form.district',
                })}
              >
                <Input disabled={disabled || disableAll} />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['address', 'street']}
                rules={disabled || disableAll ? [] : [defaultEmptyFieldError]}
                label={intl.formatMessage({
                  id: 'insurers.addresses.form.street',
                })}
              >
                <Input disabled={disabled || disableAll} />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['address', 'number']}
                label={intl.formatMessage({
                  id: 'insurers.addresses.form.number',
                })}
                rules={[
                  disabled || disableAll ? {} : defaultEmptyFieldError,
                  {
                    pattern: /^[0-9]/,
                    message: intl.formatMessage({
                      id: 'commons.form.errors.invalidField',
                    }),
                  },
                ]}
              >
                <Input disabled={disabled || disableAll} />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['address', 'complement']}
                label={intl.formatMessage({
                  id: 'insurers.addresses.form.complement',
                })}
              >
                <Input disabled={disabled || disableAll} />
              </FloatingFormItem>
            </Col>
          </Row>
        )}

        <Divider />

        <Row gutter={[16, 8]}>
          <Col span={24}>
            <Form.Item name="retentionISS" noStyle valuePropName="checked">
              <Checkbox disabled={disableAll}>
                {intl.formatMessage({
                  id: 'customers.details.generalInfo.form.issRetention',
                })}
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="retentionIRPJorCSLL"
              noStyle
              valuePropName="checked"
            >
              <Checkbox disabled={disableAll}>
                {intl.formatMessage({
                  id: 'customers.details.generalInfo.form.irpjOrCsllRetention',
                })}
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="enableLGPD"
              label={intl.formatMessage({
                id: 'customers.details.generalInfo.form.enableLgpd',
              })}
              valuePropName="checked"
              getValueFromEvent={(e: number[]) => {
                const value = e.at(-1);

                return typeof value === 'number' ? Boolean(value) : null;
              }}
              style={{ margin: 0 }}
            >
              <Checkbox.Group
                disabled={disableAll}
                value={enableLGPD !== null ? [Number(enableLGPD)] : []}
              >
                <Checkbox value={1}>Sim</Checkbox>
                <Checkbox value={0}>Não</Checkbox>
              </Checkbox.Group>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <CustomerSetMainAddressModal
        customerId={customerId}
        open={modalSetMainAddressOpened}
        onCancel={modalSetMainAddressAction.close}
      />
    </>
  );
}

export default CustomerSimplifiedForm;
